import React, { useRef, useState } from "react"
import ReactPlayer from "react-player"
import screenfull from "screenfull"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPlay,
  faPause,
  faArrowUpRightAndArrowDownLeftFromCenter,
} from "@fortawesome/sharp-light-svg-icons"

import ControlButton from "../atoms/ControlButton"
import ControlVideoProgressBar from "../atoms/ControlVideoProgressBar"
import ControlVideoVolume from "../atoms/ControlVideoVolume"
import Image from "@yumgmbh/gatsby-theme-yum-components/src/components/atoms/Image"
import MemoizedComponentWrapper from "gatsby-theme-rothenberger/src/@yumgmbh/gatsby-theme-yum-components/components/technical/MemoizedComponentWrapper"

const secondsToReadable = (inputSeconds) => {
  let val = Math.round(inputSeconds)
  let hours = Math.floor(val / 3600)
  let minutes = Math.floor((val - hours * 3600) / 60)
  let seconds = val - hours * 3600 - minutes * 60
  let time = ""

  if (hours !== 0) {
    time = hours + ":"
  }
  if (minutes !== 0 || time !== "") {
    minutes = minutes < 10 && time !== "" ? "0" + minutes : String(minutes)
    time += minutes + ":"
  }
  if (time === "") {
    time = "0:" + (seconds < 10 ? "0" + seconds : String(seconds))
  } else {
    time += seconds < 10 ? "0" + seconds : String(seconds)
  }
  return time
}

const BigPlayButton = () => {
  return (
    <div
      className={`h-20 w-20 p-4 pl-6 md:h-28 md:w-28 md:p-7 md:pl-9 bg-ro-gray-900-50o flex justify-center items-center rounded-full`}
    >
      <FontAwesomeIcon
        icon={faPlay}
        size="1x"
        className="text-ro-white h-full w-full"
      />
    </div>
  )
}

const CustomVideoPlayer = ({
  sources,
  posterImageUrl = null,
  className = "",
}) => {
  const playerRef = useRef(null)
  const [showPoster, setShowPoster] = useState(true)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [playSeconds, setPlaySeconds] = useState(0)
  const [durationSeconds, setDurationSeconds] = useState(0)
  const [loadProgress, setLoadProgress] = useState(0)
  const [playProgress, setPlayProgress] = useState(0)
  const [isSeeking, setIsSeeking] = useState(false)

  const handlePlayToggle = () => {
    setPlaying(!playing)
    setShowPoster(false)
  }

  const handleSeekMouseDown = () => {
    setIsSeeking(true)
  }
  const handleSeekChange = (e) => {
    setPlayProgress(parseFloat(e.target.value))
  }
  const handleSeekMouseUp = (e) => {
    setIsSeeking(false)
    setShowPoster(false)
    if (playerRef && playerRef.current) {
      playerRef.current.seekTo(parseFloat(e.target.value))
    }
  }

  const handleProgress = (progress) => {
    if (isSeeking === false) {
      setPlaySeconds(progress.playedSeconds)
      setLoadProgress(
        (progress.loadedSeconds / (durationSeconds / 100) / 100).toFixed(3)
      )
      setPlayProgress(
        (progress.playedSeconds / (durationSeconds / 100) / 100).toFixed(3)
      )
    }
  }

  const invokeFullscreen = () => {
    screenfull.request(document.querySelector(".custom-video-player"))
  }

  return (
    <div className={`bg-ro-gray-900 ${className}`}>
      <div className="relative">
        <div className="aspect-video">
          <button
            onClick={(e) => handlePlayToggle()}
            className="absolute inset-0 flex z-[2] items-center justify-center bg-transparent"
          >
            {showPoster && (
              <>
                <MemoizedComponentWrapper>
                  <Image
                    className="w-full"
                    image={{ filename: posterImageUrl }}
                    aspectRatio="16by9"
                    lazyLoad={false}
                  />
                  <div className="absolute z-10 inset-0 flex items-center justify-center">
                    <BigPlayButton />
                  </div>
                </MemoizedComponentWrapper>
              </>
            )}
          </button>
          <ReactPlayer
            ref={playerRef}
            className="custom-video-player absolute z-[1]"
            url={sources}
            light={false}
            width="100%"
            height="100%"
            playing={playing}
            muted={muted}
            volume={volume}
            playIcon={<BigPlayButton />}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onProgress={handleProgress}
            onDuration={setDurationSeconds}
          />
        </div>
      </div>
      <div className="w-full bg-ro-gray-150">
        <ControlVideoProgressBar
          className="w-full"
          loadProgressValue={loadProgress}
          playProgressValue={playProgress}
          handleSeekMouseDown={handleSeekMouseDown}
          handleSeekChange={handleSeekChange}
          handleSeekMouseUp={handleSeekMouseUp}
        />
        <div className="bg-ro-gray-900 flex text-ro-gray-50">
          <ControlButton onClick={() => handlePlayToggle()}>
            {playing ? (
              <FontAwesomeIcon icon={faPause} size="1x" className="w-5 h-5" />
            ) : (
              <FontAwesomeIcon icon={faPlay} size="1x" className="w-5 h-5" />
            )}
          </ControlButton>
          <ControlVideoVolume
            volume={volume}
            setVolume={setVolume}
            muted={muted}
            setMuted={setMuted}
          />
          <div className="h-12 flex items-center justify-center px-4 text-sm">
            {secondsToReadable(playSeconds)} /{" "}
            {secondsToReadable(durationSeconds)}
          </div>
          <ControlButton className="ml-auto" onClick={() => invokeFullscreen()}>
            <FontAwesomeIcon
              icon={faArrowUpRightAndArrowDownLeftFromCenter}
              size="1x"
              className="w-5 h-5"
            />
          </ControlButton>
        </div>
      </div>
    </div>
  )
}

export default CustomVideoPlayer
